import React, { useEffect, useState } from 'react';
import { Container, Header, Segment } from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Programming, UpdateProgrammingRequest } from '../../../../models/programming';
import { useBlockUI } from '../../../../shared/services/block-ui-service';
import { ToasterConfig } from '../../../../config/ToasterConfig';
import useProgrammingService from '../../../../services/backoffice/hooks/useProgrammingService';
import { ShortDto } from '../../../../models/short-dto';
import ProgrammingForm from '../components/ProgrammingForm';

// Services for dropdown data
import useClientService from '../../../../services/backoffice/hooks/useClientService';
import useVehiclePlateService from '../../../../services/backoffice/hooks/useVehiclePlateService';
import useProductsService from '../../../../services/backoffice/hooks/useProductsService';
import useDriverService from '../../../../services/backoffice/hooks/useDriverService';
import useReferenceService from '../../../../services/backoffice/hooks/useReferenceService';
import useReferenceGroupService from '../../../../services/backoffice/hooks/useReferenceGroupService';
import useRouteService from "../../../../services/backoffice/hooks/useRouteService";

const EditProgrammingPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>();
    const navigate = useNavigate();
    const blockUI = useBlockUI();
    const { updateProgramming, getProgrammingById } = useProgrammingService();
    const [programming, setProgramming] = useState<Programming>();

    // Dropdown data
    const [clients, setClients] = useState<ShortDto[]>([]);
    const [vehiclePlates, setVehiclePlates] = useState<ShortDto[]>([]);
    const [products, setProducts] = useState<ShortDto[]>([]);
    const [drivers, setDrivers] = useState<ShortDto[]>([]);
    const [references, setReferences] = useState<ShortDto[]>([]);
    const [routes, setRoutes] = useState<ShortDto[]>([]);

    // Services
    const clientService = useClientService();
    const vehiclePlateService = useVehiclePlateService();
    const productsService = useProductsService();
    const driverService = useDriverService();
    const referenceService = useReferenceService();
    const routeService = useRouteService();

    useEffect(() => {
        blockUI?.setActive(loading);
    }, [loading]);

    useEffect(() => {
        const fetchData = async () => {
            if (!id) return;
            
            try {
                setLoading(true);
                
                // Carregar a programação por ID
                const programmingData = await getProgrammingById(id);
                setProgramming(programmingData);

                // Carregar dados para os dropdowns
                const clientsResponse = await clientService.getAllClientsShortDto();
                setClients(clientsResponse || []);
                
                const vehiclePlatesResponse = await vehiclePlateService.getAllVehiclePlatesShortDto();
                setVehiclePlates(vehiclePlatesResponse || []);
                
                const productsResponse = await productsService.getProductsShort();
                setProducts(productsResponse || []);
                
                const driversResponse = await driverService.getAllDriversShortDto();
                setDrivers(driversResponse || []);
                
                const referencesResponse = await referenceService.getReferencesShort();
                setReferences(referencesResponse || []);
                
                const routesResponse = await routeService.getShortRoutes();
                setRoutes(routesResponse || []);
                
            } catch (error: any) {
                toast.error('Erro ao carregar dados', ToasterConfig.error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, [id]);

    const handleSubmit = async (formData: UpdateProgrammingRequest) => {
        if (!id) return;
        
        try {
            setLoading(true);
            await updateProgramming(id, formData);
            toast.success('Programação atualizada com sucesso', ToasterConfig.success);
            navigate('/backoffice/programming');
        } catch (error: any) {
            toast.error('Erro ao atualizar programação', ToasterConfig.error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/backoffice/programming');
    };

    return (
        <Container>
            <Header as="h3" textAlign="center" color="green">
                Editar Programação
            </Header>
            <Segment>
                {programming && (
                    <ProgrammingForm
                        initialData={programming}
                        isEdit={true}
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                        loading={loading}
                        error={error}
                        clients={clients}
                        vehiclePlates={vehiclePlates}
                        products={products}
                        drivers={drivers}
                        references={references}
                        routes={routes}
                    />
                )}
            </Segment>
        </Container>
    );
};

export default EditProgrammingPage; 